<template>
  <div class="settings-card domainsConfig weflix-admin-domain">
    <header class="settings-card__header">
      <span class="settings-card__icon" aria-hidden="true">
        <fa icon="fa-globe" />
      </span>
      <div>
        <h2 class="settings-card__title">Configurações de Domínio</h2>
        <p class="settings-card__description">Gerencie os domínios do seu sistema Weflix.</p>
      </div>
    </header>

    <div v-if="hasReady">
      <section class="settings-domain__section">
        <h3 class="settings-domain__section-title">Domínio Weflix</h3>
        <p class="settings-domain__section-description">
          O Weflix cria automaticamente um domínio com base no nome da sua loja.
        </p>
        <p class="settings-domain__alert">
          <strong>Atenção:</strong> ao atualizar o domínio, você será redirecionado e precisará entrar de novo. A
          propagação pode levar até <strong>20 minutos</strong>.
        </p>

        <div class="settings-domain__weflix-grid">
          <div class="settings-domain__field">
            <label class="settings-domain__label" for="domain-weflix-name">Nome do Domínio Weflix</label>
            <div class="settings-domain__input-group">
              <input
                id="domain-weflix-name"
                type="text"
                class="settings-domain__input"
                placeholder="Meu Domínio"
                maxlength="30"
                v-model="domainWeflix"
                @keyup="createFullDomain()"
              />
              <fa icon="fa-check" class="domain-valid" v-if="hasValidDomain === true" />
              <fa icon="fa-times" class="domain-invalid" v-if="hasValidDomain === false" />
              <button type="button" class="settings-domain__verify" @click="verifyDomain">Verificar</button>
            </div>
            <small class="settings-domain__helper">Apenas texto, sem espaços ou caracteres especiais. Máx. 30 caracteres.</small>
          </div>

          <div class="settings-domain__field">
            <label class="settings-domain__label" for="domain-weflix-final">Domínio final Weflix</label>
            <div class="settings-domain__input-group">
              <input
                id="domain-weflix-final"
                type="text"
                class="settings-domain__input"
                placeholder="https://seudominio.weflix.com.br"
                v-model="fullDomainWeflix"
                disabled
              />
              <button
                type="button"
                class="settings-domain__copy"
                aria-label="Copiar domínio"
                @click="copyToClipboard(fullDomainWeflix)"
              >
                <fa icon="fa-copy" />
              </button>
            </div>
            <small class="settings-domain__helper">Endereço público da sua loja neste domínio.</small>
          </div>
        </div>
      </section>

      <section class="settings-domain__section">
        <div class="settings-domain__title-row">
          <h3 class="settings-domain__section-title">Domínio próprio</h3>
          <span class="settings-domain__optional">Opcional</span>
        </div>
        <p class="settings-domain__section-description">
          Use um domínio que você já possui. Configure um registro <strong>CNAME</strong> (ou <strong>A</strong> para
          root) no painel do seu provedor de DNS.
        </p>
        <p class="settings-domain__alert">
          <strong>Atenção:</strong> após salvar, pode ser necessário fazer login de novo. A propagação pode levar até
          <strong>24 horas</strong>.
        </p>

        <div class="settings-domain__custom-grid">
          <div class="settings-domain__field">
            <label class="settings-domain__label" for="domain-primary">Domínio primário</label>
            <input
              id="domain-primary"
              type="text"
              class="settings-domain__input"
              placeholder="https://www.seudominio.com.br"
              v-model="domainPrimary"
            />
            <small class="settings-domain__helper">URL principal de acesso à loja.</small>
          </div>
          <div class="settings-domain__field">
            <label class="settings-domain__label" for="domain-secondary">Domínio adicional</label>
            <input
              id="domain-secondary"
              type="text"
              class="settings-domain__input"
              placeholder="https://www.seudominio.com.br"
              v-model="domainSecondary"
            />
            <small class="settings-domain__helper">Opcional — segundo endereço para a mesma loja.</small>
          </div>
        </div>

        <div class="settings-domain__dns">
          <h4 class="settings-domain__section-title">Valores para apontamento</h4>
          <p class="settings-domain__section-description">Copie e cole no seu provedor de DNS.</p>
          <div class="settings-domain__dns-grid">
            <div class="settings-domain__dns-card">
              <div>
                <span class="settings-domain__dns-badge">CNAME</span>
                <p class="settings-domain__dns-text">Aponte o subdomínio (ex.: www) para:</p>
              </div>
              <div class="settings-domain__dns-values">
                <span class="settings-domain__dns-value">app.weflix.com.br</span>
                <button type="button" class="settings-domain__copy" aria-label="Copiar CNAME" @click="copyToClipboard('app.weflix.com.br')">
                  <fa icon="fa-copy" />
                </button>
              </div>
            </div>
            <div class="settings-domain__dns-card">
              <div>
                <span class="settings-domain__dns-badge">Registro A</span>
                <p class="settings-domain__dns-text">Para o domínio raiz (@), use um destes IPs:</p>
              </div>
              <div class="settings-domain__dns-values settings-domain__dns-values--stack">
                <div class="settings-domain__dns-line">
                  <span class="settings-domain__dns-value">35.174.239.148</span>
                  <button type="button" class="settings-domain__copy" aria-label="Copiar IP" @click="copyToClipboard('35.174.239.148')">
                    <fa icon="fa-copy" />
                  </button>
                </div>
                <div class="settings-domain__dns-line">
                  <span class="settings-domain__dns-value">54.208.52.130</span>
                  <button type="button" class="settings-domain__copy" aria-label="Copiar IP" @click="copyToClipboard('54.208.52.130')">
                    <fa icon="fa-copy" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="settings-domain__tutorial">
          <fa icon="fa-book-open" />
          <p>
            Dificuldade com os apontamentos?
            <a href="" class="settings-domain__tutorial-link">Siga o tutorial passo a passo</a>.
          </p>
        </div>
      </section>

      <div class="settings-card__actions">
        <button type="button" class="settings-save" @click="editDomain">Salvar alterações</button>
      </div>
    </div>

    <Loader :hasReady="hasReady" />
  </div>
</template>

<script>
import apiDomain from '@/utils/Weflix/domain';
import { mapState } from 'vuex';
import Loader from '@/components/Weflix/Loader';
import apiWebsite from '@/utils/Weflix/website';
import { toast } from 'vue3-toastify';

function weflixSlug(url) {
  if (!url) return '';
  const host = String(url).trim().replace(/^https?:\/\//i, '').split('/')[0].replace(/^www\./i, '');
  const suffix = '.weflix.com.br';
  if (host.endsWith(suffix)) return host.slice(0, -suffix.length);
  if (!host.includes('.')) return host;
  return host.split('.')[0] || '';
}

function weflixFullDomain(slug) {
  const name = String(slug || '').replace(/[^\w]/g, '').toLowerCase();
  return name ? `https://${name}.weflix.com.br` : '';
}

export default {
  data() {
    return {
      domainWeflix: '',
      fullDomainWeflix: '',
      domainPrimary: '',
      domainSecondary: '',
      hasReady: false,
      hasData: false,
      hasValidDomain: null,
    };
  },
  computed: {
    ...mapState(['token']),
  },
  components: {
    Loader,
  },
  methods: {
    async getDomain() {
      try {
        const response = await apiDomain.getDomain(this.token);
        this.hasReady = true;
        this.domainWeflix = weflixSlug(response.url);
        this.fullDomainWeflix = weflixFullDomain(this.domainWeflix);
        this.domainPrimary = response.url1;
        this.domainSecondary = response.url2;
        this.hasData = response.url != null || response.url != '' ? true : false;
      } catch (error) {
        console.error('Erro ao obter dados do dominio:', error);
      }
    },
    async editDomain() {
      this.hasReady = false;
      this.createFullDomain();
      const payload = {
        url: this.fullDomainWeflix,
        url1: this.domainPrimary,
        url2: this.domainSecondary,
      };
      try {
        await apiDomain.editDomain(this.token, payload);
        this.hasReady = true;
        this.notify('Domínio atualizado com sucesso!', 'success');
      } catch (error) {
        console.error(error);
        this.hasReady = true;
        this.notify('Erro ao atualizar domínio!', 'error');
      }
    },
    createFullDomain() {
      this.domainWeflix = String(this.domainWeflix || '').replace(/[^\w\s]/gi, '').replace(/\s+/g, '').toLowerCase();
      this.fullDomainWeflix = weflixFullDomain(this.domainWeflix);
    },
    async verifyDomain() {
      const payload = {
        url: this.fullDomainWeflix,
      };
      try {
        const response = await apiWebsite.verifyDomaintData(this.token, payload);
        this.hasValidDomain = response;
      } catch (error) {
        console.error('Erro ao verificar dominio:', error);
      }
    },
    copyToClipboard(text) {
      if (text == null || text === '') return;
      navigator.clipboard.writeText(String(text));
      this.notify('Copiado para a área de transferência.', 'success');
    },
    notify(message, type = 'info') {
      toast(message, {
        autoClose: 3000,
        position: 'top-right',
        theme: 'light',
        limit: 1,
        type: type,
        dangerouslyHTMLString: true,
      });
    },
  },
  created() {
    this.getDomain();
  },
};
</script>

<style scoped>
.settings-card {
  background: #fff;
  border: 1px solid #e7eaf2;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 3px 14px rgba(20, 25, 60, 0.03);
  text-align: left;
}

.settings-card__header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 12px;
}

.settings-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f1eeff;
  color: #4327c9;
}

.settings-card__icon :deep(svg.svg-inline--fa) {
  color: #4327c9;
}

.settings-domain__copy :deep(svg.svg-inline--fa),
.settings-domain__tutorial :deep(svg.svg-inline--fa) {
  color: inherit;
}

.domain-valid {
  color: #166534;
}

.domain-invalid {
  color: #991b1b;
}

.settings-card__title,
.settings-domain__section-title,
.settings-domain__label,
.settings-domain__helper,
.settings-domain__section-description,
.settings-domain__alert,
.settings-domain__dns-text,
.settings-domain__tutorial {
  text-align: left;
}

.settings-card__title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #11143d;
}

.settings-card__description {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
}

.settings-domain__section {
  padding: 16px;
  background: #fafbfe;
  border: 1px solid #e7eaf2;
  border-radius: 10px;
  margin-bottom: 12px;
}

.settings-domain__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.settings-domain__section-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #11143d;
}

.settings-domain__optional {
  font-size: 11px;
  font-weight: 600;
  color: #4327c9;
  background: #f1eeff;
  border-radius: 999px;
  padding: 2px 8px;
}

.settings-domain__section-description {
  margin: 4px 0 10px;
  font-size: 12px;
  line-height: 1.5;
  color: #64748b;
}

.settings-domain__alert {
  padding: 9px 12px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 7px;
  color: #92400e;
  font-size: 12px;
  line-height: 1.45;
  margin: 0 0 12px;
}

.settings-domain__weflix-grid,
.settings-domain__custom-grid,
.settings-domain__dns-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.settings-domain__dns-grid {
  gap: 12px;
}

.settings-domain__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.settings-domain__label {
  font-size: 12px;
  font-weight: 500;
  color: #344054;
}

.settings-domain__input-group,
.settings-domain__dns-line,
.settings-domain__dns-values {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.settings-domain__dns-values--stack {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

.settings-domain__input {
  box-sizing: border-box;
  height: 44px;
  min-height: 44px;
  flex: 1;
  min-width: 0;
  width: 100%;
  border: 1px solid #d9dfea;
  border-radius: 8px;
  padding: 0 14px;
  background: #fff;
  font-size: 14px;
  line-height: 1.4;
  color: #11143d;
}

.settings-domain__input:disabled {
  background: #f8f9fc;
  color: #344054;
}

.settings-domain__input:focus {
  outline: 2px solid rgba(84, 56, 232, 0.16);
  outline-offset: 1px;
  border-color: #5438e8;
}

.settings-domain__helper {
  color: #8490a5;
  font-size: 11px;
  line-height: 1.4;
}

.settings-domain__verify {
  height: 44px;
  padding: 0 16px;
  border-radius: 7px;
  border: 1px solid #4327c9;
  background: #fff;
  color: #4327c9;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
}

.settings-domain__verify:hover {
  background: #4327c9;
  color: #fff;
}

.settings-domain__copy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border: 1px solid #e7eaf2;
  border-radius: 7px;
  background: #fff;
  color: #4327c9;
  cursor: pointer;
}

.domain-valid {
  color: #166534;
}

.domain-invalid {
  color: #991b1b;
}

.settings-domain__dns {
  margin-top: 14px;
}

.settings-domain__dns-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  min-height: 76px;
  padding: 12px;
  background: #fff;
  border: 1px solid #e7eaf2;
  border-radius: 9px;
}

.settings-domain__dns-badge {
  display: inline-block;
  margin-bottom: 6px;
  padding: 3px 8px;
  border-radius: 6px;
  background: #f1eeff;
  color: #4327c9;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
}

.settings-domain__dns-text {
  margin: 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.settings-domain__dns-value {
  overflow-wrap: anywhere;
  padding: 8px 10px;
  border-radius: 6px;
  background: #eef0f3;
  color: #344054;
  font-size: 12px;
}

.settings-domain__tutorial {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 10px 12px;
  background: #f8f9fc;
  border: 1px solid #e7eaf2;
  border-radius: 7px;
  font-size: 12px;
  color: #64748b;
}

.settings-domain__tutorial p {
  margin: 0;
}

.settings-domain__tutorial-link {
  color: #4327c9;
  font-weight: 600;
}

.settings-card__actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
}

button.settings-save {
  height: 42px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  background: #4327c9;
  color: #fff !important;
  -webkit-text-fill-color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

button.settings-domain__verify {
  color: #4327c9;
}

.settings-save:hover {
  background: #341bae;
}

@media (max-width: 900px) {
  .settings-domain__weflix-grid,
  .settings-domain__custom-grid,
  .settings-domain__dns-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .settings-card {
    padding: 16px;
  }

  .settings-domain__section {
    padding: 12px;
  }

  .settings-domain__input-group,
  .settings-domain__dns-card,
  .settings-domain__dns-line {
    flex-wrap: wrap;
  }

  .settings-domain__dns-card {
    flex-direction: column;
  }

  .settings-save,
  .settings-domain__verify {
    width: 100%;
  }
}
</style>
